import { InfoWindowF, MarkerClustererF, MarkerF } from '@react-google-maps/api'
import {
  ClustererOptions,
  ClusterIconInfo,
  ClusterIconStyle,
  MarkerExtended
} from '@react-google-maps/marker-clusterer'
import { useState } from 'react'

import { MultiPointMapGeo } from './types'

const baseClusterIconStyle: Pick<
  ClusterIconStyle,
  'url' | 'textColor' | 'fontFamily' | 'fontStyle' | 'fontWeight'
> = {
  url: '/img/mapMarkers/marker.png',
  textColor: '#fff',
  fontFamily: 'Arial',
  fontStyle: 'normal',
  fontWeight: '700'
}

const smallClusterIconStyle: ClusterIconStyle = {
  ...baseClusterIconStyle,
  height: 52,
  width: 52,
  textSize: 16
}

const mediumClusterIconStyle: ClusterIconStyle = {
  ...baseClusterIconStyle,
  height: 68,
  width: 68,
  textSize: 18
}

const largeClusterIconStyle: ClusterIconStyle = {
  ...baseClusterIconStyle,
  height: 90,
  width: 90,
  textSize: 22
}

const clusterIconStyles = [
  smallClusterIconStyle,
  mediumClusterIconStyle,
  largeClusterIconStyle
]

const markerLengthToIndex = (length: number): number => {
  if (length >= 10) {
    return 3
  }
  if (length >= 4) {
    return 2
  }
  return 1
}

const markerClustererCalculator = (
  markers: MarkerExtended[]
): ClusterIconInfo => ({
  text: `${markers.length}`,
  index: markerLengthToIndex(markers.length),
  title: ''
})

const markerClustererOptions: ClustererOptions = {
  averageCenter: true,
  calculator: markerClustererCalculator,
  maxZoom: 18,
  styles: clusterIconStyles,
  zoomOnClick: true
}

function createKey(location: MultiPointMapGeo, idx: number) {
  return `${location.info?.title}_${location.place_id}_${location.lat}_${location.lng}_${idx}`
}

export const ClusterPoints = ({
  pointData,
  clustered = false,
  pinIcon,
  renderInfoContent
}: {
  pointData: MultiPointMapGeo[]
  clustered?: boolean
  pinIcon?: string
  renderInfoContent?: (point: MultiPointMapGeo) => JSX.Element
}) => {
  const [selectedMarker, setSelectedMarker] = useState<MultiPointMapGeo>()

  return (
    <MarkerClustererF options={markerClustererOptions}>
      {clusterer => (
        <div>
          {pointData
            .filter(
              p =>
                typeof p.lat === 'number' &&
                !Number.isNaN(p.lat) &&
                typeof p.lng === 'number' &&
                !Number.isNaN(p.lng)
            )
            .map((p, idx) => {
              return (
                <MarkerF
                  key={createKey(p, idx)}
                  data-testid={createKey(p, idx)}
                  position={p}
                  clusterer={clustered ? clusterer : undefined}
                  onClick={() => setSelectedMarker(p)}
                  icon={
                    pinIcon
                      ? {
                          url: pinIcon,
                          size: new google.maps.Size(35, 44)
                        }
                      : undefined
                  }
                >
                  {selectedMarker &&
                  selectedMarker.info &&
                  selectedMarker?.place_id === p.place_id ? (
                    <InfoWindowF
                      onCloseClick={() => {
                        setSelectedMarker(undefined)
                      }}
                      position={{
                        lat: selectedMarker.lat,
                        lng: selectedMarker.lng
                      }}
                      options={{
                        headerContent: selectedMarker.info.title
                      }}
                    >
                      {renderInfoContent ? (
                        renderInfoContent(p)
                      ) : (
                        <div className='flex flex-col gap-1'>
                          {selectedMarker.info.description ? (
                            <span>{selectedMarker.info.description}</span>
                          ) : null}
                        </div>
                      )}
                    </InfoWindowF>
                  ) : null}
                </MarkerF>
              )
            })}
        </div>
      )}
    </MarkerClustererF>
  )
}
